import { createQueryKeyStore } from '@lukemorales/query-key-factory'
import { useQuery } from '@tanstack/react-query'
import { usePublicClient } from 'wagmi'

import { active_chain } from '@repo/common/blockchain/config.ts'
import { aggregatorV3InterfaceABI } from '@repo/common/queries/blockchain_abis/AggregatorV3'

export type ExchangeSymbol = 'XPE' | 'TECH' | 'USD' | 'USDC' | 'ETH' | 'BTC'

export const keys = createQueryKeyStore({
  exchange_rate_source: {
    prices_subgraph: null,
    fission_dex: null,
    chainlink: (pair: string) => [pair],
  },
})

type BlockchainName = 'Hardhat' | 'Sepolia' | 'Mainnet'

const CHAINLINK_PAIR_ADDRESSES: Record<
  BlockchainName,
  Record<'ETH/USD', string | null>
> = {
  // todo migrate this into crypto_contracts (perhaps?) and probably somehow stub it for hardhat?? really not very important
  Hardhat: {
    'ETH/USD': null,
  },
  Sepolia: {
    'ETH/USD': '0x694AA1769357215DE4FAC081bf1f309aDC325306',
  },
  Mainnet: {
    'ETH/USD': '0x5147eA642CAEF7BD9c1265AadcA78f997AbB9649',
  },
}

// export const SUPPORTED_EXCHANGE_RATE_TOKENS = ['XPE', 'TECH'] as const
// export type SupportedExchangeRateToken = typeof SUPPORTED_EXCHANGE_RATE_TOKENS[number]

export interface ExchangeRate {
  usdPrice: number
  isLoading: boolean
  isSuccess: boolean
  error: unknown
  refetch?: () => void
  isFetching?: boolean
}

// /**
//  * Utility function to check if a token is supported for exchange rates
//  *
//  * @param token The token symbol to check
//  * @returns True if the token is supported, false otherwise
//  */
// export function isExchangeRateSupported(token: string): boolean {
//   return SUPPORTED_EXCHANGE_RATE_TOKENS.includes(token as ExchangeSymbol)
// }

// export const TOKEN_PRICE_METHODS: Record<SupportedExchangeRateToken, ExchangeRatePriceMethod> = {
//   'XPE': 'swap',
//   'TECH': 'subgraph',
//   // Add more tokens here as they become supported
// }
// /**
//  * Hook to retrieve exchange rate information for a specific token
//  * All prices are assumed to be in USD
//  *
//  * @param source The token symbol to get exchange rate for
//  * @param options Optional configuration options for the price fetching
//  * @returns Exchange rate information including USD price and loading state
//  */
// export function useExchangeRate(
//   source: ExchangeRateTokenSource,
//   options?: { forceMethod?: ExchangeRatePriceMethod }
// ): ExchangeRate {
//   // Determine which price resolution method to use
//   const method = options?.forceMethod || TOKEN_PRICE_METHODS[source] || 'subgraph'
//
//   // Use the appropriate price method based on the token and options
//   if (method === 'swap') {
//     // For tokens that have swap prices available (currently just XPE)
//     if (source === 'XPE') {
//       return useVVCExchangeRate()
//     }
//   }
//
//   if (method === 'subgraph') {
//     // For tokens that have subgraph prices available
//     if (source === 'TECH') {
//       return useTechExchangeRate()
//     }
//
//     // For future tokens using the subgraph method, we could implement a generic subgraph price fetcher
//     // return useSubgraphExchangeRate(source)
//   }
//
//   // Default fallback for unknown tokens or methods
//   return {
//     usdPrice: 0,
//     isLoading: false,
//     isSuccess: false,
//     error: new Error(`Unsupported token source: ${source} or method: ${method}`)
//   }
// }

// function useVVCExchangeRate(props: { enable: boolean }): ExchangeRate {
//   return useFissionDexExchangeRate('XPE', 'USDC', props)
// }
// /**
//  * Utility function to convert an amount from one currency to another using exchange rates
//  *
//  * @param amount The amount to convert
//  * @param fromSource The source currency
//  * @param toSource The target currency
//  * @returns The converted amount, or null if conversion fails
//  */
// export function useConvertCurrency(
//   amount: number,
//   fromSource: ExchangeRateTokenSource,
//   toSource: ExchangeRateTokenSource
// ): {
//   convertedAmount: number | null,
//   isLoading: boolean,
//   isSuccess: boolean,
//   error: unknown
// } {
//   const fromRate = useExchangeRate(fromSource)
//   const toRate = useExchangeRate(toSource)
//
//   const isLoading = fromRate.isLoading || toRate.isLoading
//   const isSuccess = fromRate.isSuccess && toRate.isSuccess
//
//   // Handle any errors from either rate
//   const error = fromRate.error || toRate.error
//
//   // Calculate the converted amount if both rates are available
//   const convertedAmount = useMemo(() => {
//     if (!isSuccess || fromRate.usdPrice === 0) return null
//
//     // Convert from source currency to USD, then from USD to target currency
//     const amountInUsd = amount * fromRate.usdPrice
//
//     if (toRate.usdPrice === 0) return null
//     return amountInUsd / toRate.usdPrice
//   }, [amount, fromRate.usdPrice, toRate.usdPrice, isSuccess])
//
//   return {
//     convertedAmount,
//     isLoading,
//     isSuccess,
//     error
//   }
// }

/**
 * Uses the latest price data from the subgraph, sorted by block number
 */
export function useChainlinkOracleRate(
  pairKey: 'ETH/USD',
  props: { enable: boolean },
): ExchangeRate {
  const client = usePublicClient()
  const pairAddress = CHAINLINK_PAIR_ADDRESSES[active_chain.name][pairKey]
  const { data, isLoading, isSuccess, error, isFetching } = useQuery({
    enabled: props.enable && !!client && !!pairAddress,
    queryKey: keys.exchange_rate_source.chainlink(pairKey).queryKey,
    queryFn: async () => {
      try {
        if (!client || !pairAddress) {
          throw new Error(
            `Cannot fetch Chainlink price: client or address missing for ${pairKey}`,
          )
        }

        const { result } = await client.simulateContract({
          address: pairAddress as `0x${string}`,
          abi: aggregatorV3InterfaceABI,
          functionName: 'latestRoundData',
        })

        const decimalsResult = await client.readContract({
          address: pairAddress as `0x${string}`,
          abi: aggregatorV3InterfaceABI,
          functionName: 'decimals',
        })

        const decimals = Number(decimalsResult)
        const price = Number(result[1]) / 10 ** decimals

        return {
          usdPrice: price,
          updatedAt: Number(result[3]),
          roundId: Number(result[0]),
        }
      } catch (err) {
        console.error(
          `Error fetching ${pairKey} price data from Chainlink:`,
          err,
        )
        throw err
      }
    },
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  })

  return {
    usdPrice: data?.usdPrice ?? 0,
    isLoading,
    isSuccess,
    error,
    isFetching,
  }
}
